// Features: instanced trees, mountain peaks, reeds, shrubs and stones standing on the terrain.
//
//   mesh worker ──► per chunk and LOD: prop records in 8×8-hex blocks (placement.ts)
//   TerrainChunks.shown() ──► blocks of the chunks on screen ──► frustum cull
//        ──► one merged InstancedMesh per model slot (≈20 draw calls for every prop on screen)
//
// The merge reruns only when the set of visible blocks changes, so a still camera costs nothing.
// Every prop shares the terrain's painted light (PaintedMaterial). Trees sway a little; peaks take
// their glacier cap from data in the shader (snow line per instance, heights.ts capFraction).

import * as THREE from 'three/webgpu';
import {
  Fn, attribute, cos, dot, float, fwidth, max, mix, normalWorld, positionGeometry, positionLocal, positionWorld, select, sin, smoothstep, vec2, vec3,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { WORLD_W } from '../../core/grid.ts';
import type { HexData } from '../hexdata.ts';
import { PaintedMaterial, createPaintStyle, type PaintStyle } from '../lighting.ts';
import { drapesOnProps, type SurfaceChain, type SurfaceCtx, type SurfaceLayer } from '../surface.ts';
import { rgb, screen, screenNode } from '../tsl/color.ts';
import { worldFbm } from '../tsl/noise.ts';
import { hexFrag, wrapXNode } from '../tsl/hex.ts';
import { peakMesh, peakSpec } from './peakGeo.ts';
import { BLOCK_REC, PEAK_FILE_PREFIX, REC, SLOTS, peakKept, peakThin, type FeatureData, type SlotDef } from './placement.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

export interface FeatureDeps {
  /** Owner colours for building roofs and flags (actor colour texture). */
  data: HexData;
  /** Seconds since start. */
  time: F;
  /** URL of public/assets (e.g. '/assets'). */
  assetBase: string;
  /** Surface layers: borders, fronts and highlights drape over the peaks (surface.ts drapesOnProps). */
  surface: SurfaceChain;
  /** Camera height above ground, hex radii. */
  camHeight: F;
}

/** A block chosen for this frame; `peaksOnly` blocks are past the small-prop distance. */
interface VisibleBlock {
  data: FeatureData;
  block: number;
  dx: number;
  peaksOnly: boolean;
  /** Keep every n-th small prop (1 = all). Buildings are never thinned. */
  stride: number;
  /** Peak thinning level for the camera height (placement.ts peakThin). */
  thin: number;
}

/** Whether record r of a block survives the block's distance thinning. */
function kept(v: VisibleBlock, r: number, def: SlotDef): boolean {
  const kind = def.kind;
  if (kind === 'peak') {
    return peakKept(v.thin, def.role, v.data.rec[r * REC + 6]);
  }
  if (v.peaksOnly) {
    return false;
  }
  return kind === 'building' || (r - v.data.blocks[v.block]) % v.stride === 0;
}

/** A chunk on screen: its props and the ±WORLD_W copy it is drawn at. */
export interface ShownChunk {
  /** Unique per chunk and LOD. */
  id: number;
  /** Terrain chunk key (mesher.ts chunkKey). */
  key: number;
  /** Props the worker placed on this chunk (null for none). */
  features: FeatureData | null;
  shift: number;
  /** True for the MID LOD (props are clumps). */
  mid: boolean;
}

// MID-LOD clumps show below this camera height; NEAR props always show (NEAR chunks only exist
// close to the camera).
const MID_MAX_HEIGHT = 120;
// Distance culling from the camera target (world units, and per unit of camera height): small
// props go first, peaks carry the silhouette of ranges much farther.
const SMALL_FAR = [28, 1.1] as const;
const THIN_FROM = 0.5; // beyond this share of SMALL_FAR, small props are thinned to every other one
const PEAK_FAR = [80, 3.5] as const;
const CITY_MAX_HEIGHT = 110;
const PEAK_THIN_LEVELS = 3; // placement.ts peakThin returns 0, 1 or 2
const CITY_ID_OFFSET = 1 << 20; // keeps city block ids apart from chunk prop ids in the signature
const INITIAL_CAPACITY = 4096;
const SWAY = 0.1; // world units of sway per (model unit of height)², at the tree top
const SWAY_HZ = [0.27, 0.19] as const;
const TREE_TINT_JITTER = 0.18;
// Foliage grade: the pack's leaf greens read lime under the painted light; deepen and cool them.
const FOLIAGE_GRADE = [0.8, 0.84, 0.98] as const;
const ROUGHNESS = 0.85;
// Props keep only a faint rim: on objects a few pixels tall the terrain's rim light turns into
// white sparkles that the tilt-shift blur then spreads.
const PROP_RIM = 0.04;
const PROP_SPECULAR = 0.04;
const TEAM_GAIN = 1.15; // roofs: the owner colour, lifted back from the painted light

// City buildings: warm stone walls, roofs in the owner's colour. Only a few models mark their roof
// parts (_TEAM), so the roof is also any face pointing up.
const WALL_STONE = '#d6c7a6';
const WALL_SHADE = [0.62, 1.12] as const; // wall brightness from the vertex colour's darkest to lightest
const LUMA = [0.3, 0.59, 0.11] as const;
const ROOF_UP = [0.62, 0.86] as const;
// From this camera height cities under 300k pop flatten to a decal; fully flat by the second.
const DECAL_FROM = [40, 55] as const;
const DECAL_HEIGHT = 0.12;

// Peak colours: the models carry rock only; the glacier cap comes from data (heights.ts capFraction)
// as a snow line per instance. Snow albedo stays below the tone map's shoulder.
const SNOW_LIT = '#f4f8fc';
const SNOW_SHADE = '#c6d4e6';
const SNOW_EDGE = 0.035;
const SNOW_NOISE = 0.14;
const SNOW_NOISE_FREQ = 3;
// Steep faces keep the glacier too (it clings and fills the gullies), so the cap reads by area.
const SNOW_STEEP = 0.85;
// High alpine rock cools toward blue-grey (linear multiplier), from this share of the model height.
const ALPINE_COOL = [0.88, 0.96, 1.1] as const;
const COOL_FROM = [0.3, 0.85] as const;

interface SlotMesh {
  def: SlotDef;
  mesh: THREE.InstancedMesh;
  extra: THREE.InstancedBufferAttribute;
  capacity: number;
}

export interface FeatureStats {
  instances: number;
  blocks: number;
  rebuilds: number;
}

export class FeatureLayer {
  readonly group = new THREE.Group();
  private readonly deps: FeatureDeps;
  private readonly style: PaintStyle;
  private readonly slots: Array<SlotMesh | null> = SLOTS.map(() => null);
  private cities = new Map<number, FeatureData>();
  private citiesVersion = 0;
  private readonly materials = new Map<SlotDef['kind'], PaintedMaterial>();
  private readonly frustum = new THREE.Frustum();
  private readonly projView = new THREE.Matrix4();
  private readonly box = new THREE.Box3();
  private signature = -1;
  private warming = false;
  private shadows: 'on' | 'off' = 'on';
  private last: FeatureStats = { instances: 0, blocks: 0, rebuilds: 0 };
  private rebuilds = 0;
  private peakLayers: SurfaceLayer[] = [];
  private peaksStale = false;
  private readonly offSurface: () => void;

  constructor(deps: FeatureDeps) {
    this.deps = deps;
    this.style = createPaintStyle();
    this.style.rimStrength.value = PROP_RIM;
    this.style.specular.value = PROP_SPECULAR;
    this.group.name = 'features';

    // The peaks paint the draped layers themselves: rebuild their material only when that set changes.
    this.peakLayers = drapedLayers(deps.surface);
    this.offSurface = deps.surface.onChange(() => {
      this.peaksStale = !sameLayers(this.peakLayers, drapedLayers(deps.surface));
    });
  }

  /** Load every slot's model. Missing files leave their slot empty (logged, not fatal). */
  async load(): Promise<void> {
    const loader = new GLTFLoader();
    await Promise.all(SLOTS.map(async (def, i) => {
      if (def.file.startsWith(PEAK_FILE_PREFIX)) {
        this.slots[i] = this.makeSlot(def, peakGeometry(def.file), INITIAL_CAPACITY);
        return;
      }
      try {
        const gltf = await loader.loadAsync(`${this.deps.assetBase}/${def.file}`);
        const geometry = firstGeometry(gltf.scene);
        if (!geometry) {
          throw new Error('no mesh');
        }
        this.slots[i] = this.makeSlot(def, geometry, INITIAL_CAPACITY);
      } catch (err) {
        console.warn(`features: ${def.file}: ${String(err)}`);
      }
    }));
  }

  setShadows(on: 'on' | 'off'): void {
    this.shadows = on;
    for (const s of this.slots) {
      if (s) {
        s.mesh.castShadow = castsShadow(s.def, this.shadows);
      }
    }
  }

  /** City buildings per terrain chunk key (features/cities.ts), replacing the previous set. */
  setCities(cities: Map<number, FeatureData>): void {
    this.cities = cities;
    this.citiesVersion++;
  }

  /**
   * Show the props of the chunks on screen around the camera target (tx, tz); merges again only
   * when the set of visible blocks changes.
   */
  update(camera: THREE.Camera, shown: readonly ShownChunk[], tx: number, tz: number, height: number): void {
    if (this.warming) {
      return;
    }
    if (this.peaksStale) {
      this.refreshPeaks();
    }
    this.projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projView);
    const smallFar = SMALL_FAR[0] + SMALL_FAR[1] * height;
    const peakFar = PEAK_FAR[0] + PEAK_FAR[1] * height;
    const thin = peakThin(height);

    // Visible blocks, and a signature of the set.
    const visible: VisibleBlock[] = [];
    let sig = Math.imul(2166136261 ^ this.citiesVersion, 16777619);
    const collect = (data: FeatureData, id: number, shift: number): void => {
      const b = data.blocks;
      const dx = shift * WORLD_W;
      for (let i = 0; i < b.length; i += BLOCK_REC) {
        this.box.min.set(b[i + 2] + dx, b[i + 3], b[i + 4]);
        this.box.max.set(b[i + 5] + dx, b[i + 6], b[i + 7]);
        const far = Math.hypot(Math.max(this.box.min.x - tx, 0, tx - this.box.max.x), Math.max(this.box.min.z - tz, 0, tz - this.box.max.z));
        if (far > peakFar || !this.frustum.intersectsBox(this.box)) {
          continue;
        }
        const peaksOnly = far > smallFar;
        const stride = far > smallFar * THIN_FROM ? 2 : 1;
        visible.push({ data, block: i, dx, peaksOnly, stride, thin });
        sig = Math.imul(sig ^ ((((id * 64 + i / BLOCK_REC) * 4 + shift + 1) * 4 + (peaksOnly ? 2 : 0) + stride) * PEAK_THIN_LEVELS + thin), 16777619);
      }
    };
    for (const c of shown) {
      if (c.features && !(c.mid && height > MID_MAX_HEIGHT)) {
        collect(c.features, c.id, c.shift);
      }
      const city = height < CITY_MAX_HEIGHT ? this.cities.get(c.key) : undefined;
      if (city) {
        collect(city, CITY_ID_OFFSET + c.key, c.shift);
      }
    }
    sig = (sig ^ visible.length) >>> 0;
    if (sig === this.signature) {
      return;
    }
    this.signature = sig;
    this.rebuild(visible);
  }

  /**
   * Show one instance of every prop kind at (x, y, z) so each material's pipeline compiles now
   * rather than on the first close zoom. update() leaves them alone until the returned function
   * runs: the real frames in between draw their shadow pass with every slot present (each slot's
   * shadow shader builds in the context the frames use, not all at once on the first close zoom).
   * The next update() then restores the real instances.
   */
  warm(x: number, y: number, z: number): () => void {
    for (const s of this.slots) {
      if (!s) {
        continue;
      }
      writeMatrix(s.mesh.instanceMatrix.array as Float32Array, 0, x, y, z, 0, 1, 1);
      s.mesh.count = Math.max(s.mesh.count, 1);
      s.mesh.visible = true;
      s.mesh.instanceMatrix.needsUpdate = true;
    }

    // No real signature equals this, so the next update() rebuilds every slot.
    this.signature = -1;
    this.warming = true;
    return () => {
      this.warming = false;
    };
  }

  stats(): FeatureStats {
    return this.last;
  }

  dispose(): void {
    this.offSurface();
    for (const s of this.slots) {
      if (s) {
        s.mesh.geometry.dispose();
        s.mesh.dispose();
      }
    }
    for (const m of this.materials.values()) {
      m.dispose();
    }
    this.group.clear();
  }

  private rebuild(visible: readonly VisibleBlock[]): void {
    this.rebuilds++;

    // Count per slot, grow any slot that is too small.
    const counts = new Array<number>(SLOTS.length).fill(0);
    for (const v of visible) {
      const b = v.data.blocks;
      const start = b[v.block];
      const n = b[v.block + 1];
      for (let r = start; r < start + n; r++) {
        const slot = v.data.rec[r * REC];
        if (kept(v, r, SLOTS[slot])) {
          counts[slot]++;
        }
      }
    }
    counts.forEach((n, i) => {
      const s = this.slots[i];
      if (s && n > s.capacity) {
        this.slots[i] = this.makeSlot(s.def, s.mesh.geometry, Math.ceil(n * 1.5), s);
      }
    });

    // Write instance matrices and extras.
    const fill = new Array<number>(SLOTS.length).fill(0);
    for (const v of visible) {
      const b = v.data.blocks;
      const rec = v.data.rec;
      const start = b[v.block];
      const n = b[v.block + 1];
      for (let r = start; r < start + n; r++) {
        const at = r * REC;
        const s = this.slots[rec[at]];
        if (!s || !kept(v, r, s.def)) {
          continue;
        }
        const i = fill[rec[at]]++;
        const x = rec[at + 1] + v.dx;
        writeMatrix(s.mesh.instanceMatrix.array as Float32Array, i * 16, x, rec[at + 2], rec[at + 3], rec[at + 4], rec[at + 5], rec[at + 6]);
        const e = s.extra.array as Float32Array;
        e[i * 2] = rec[at + 7];
        e[i * 2 + 1] = seedOf(rec[at + 1], rec[at + 3]);
      }
    }

    let instances = 0;
    this.slots.forEach((s, i) => {
      if (!s) {
        return;
      }
      const n = fill[i];
      instances += n;
      s.mesh.count = n;
      s.mesh.visible = n > 0;
      if (n === 0) {
        return;
      }
      s.mesh.instanceMatrix.clearUpdateRanges();
      s.mesh.instanceMatrix.addUpdateRange(0, n * 16);
      s.mesh.instanceMatrix.needsUpdate = true;
      s.extra.clearUpdateRanges();
      s.extra.addUpdateRange(0, n * 2);
      s.extra.needsUpdate = true;
    });
    this.last = { instances, blocks: visible.length, rebuilds: this.rebuilds };
  }

  private makeSlot(def: SlotDef, geometry: THREE.BufferGeometry, capacity: number, old?: SlotMesh): SlotMesh {
    if (old) {
      this.group.remove(old.mesh);
      old.mesh.dispose();
    }
    const extra = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 2), 2);
    extra.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('iExtra', extra);
    const mesh = new THREE.InstancedMesh(geometry, this.material(def.kind), capacity);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.visible = false;
    mesh.frustumCulled = false;
    mesh.castShadow = castsShadow(def, this.shadows);
    mesh.receiveShadow = true;
    mesh.name = `feature:${def.file}`;
    this.group.add(mesh);
    return { def, mesh, extra, capacity };
  }

  /**
   * Peak colour: rock and cap, then the draped surface layers (borders, fronts, highlights) as the
   * terrain evaluates them, so political lines run unbroken over the relief.
   */
  private paintPeak(m: PaintedMaterial): void {
    const extra = attribute('iExtra', 'vec2') as unknown as Node<'vec2'>;
    const vertexColor = attribute('color', 'vec3') as V3;
    const alpine = attribute('alpine', 'float') as unknown as F;
    const layers = this.peakLayers;
    const deps = this.deps;
    const emissiveOut = vec3(0).toVar('peakEmissive');

    m.colorNode = Fn(() => {
      const base = peakColor(vertexColor, alpine, extra.x).toVar('peakBase');
      if (layers.length === 0) {
        return base;
      }

      const world = positionWorld;
      const ctx: SurfaceCtx = {
        hex: hexFrag(world.xz),
        world,
        xz: vec2(wrapXNode(world.x), world.z),
        normal: normalWorld,
        px: fwidth(world.xz).length(),
        color: base,
        base,
        roughness: float(ROUGHNESS),
        data: deps.data,
        time: deps.time,
        camHeight: deps.camHeight,
      };
      let emissive: V3 = vec3(0);
      for (const layer of layers) {
        if (layer.color) {
          ctx.color = layer.color(ctx).toVar(`peak_${layer.name.replace(/[^A-Za-z0-9]/g, '_')}`);
        }
        if (layer.emissive) {
          emissive = emissive.add(layer.emissive(ctx));
        }
      }
      emissiveOut.assign(emissive);
      return ctx.color;
    })();
    m.emissiveNode = emissiveOut;
  }

  /**
   * Buildings: stone walls shaded by the model's vertex colour, roofs in the owner's colour. The
   * owner comes per instance (extra.x; negative for a city under 300k pop, cities.ts), and those
   * small cities squash to a flat decal as the camera rises.
   */
  private paintBuilding(m: PaintedMaterial): void {
    const extra = attribute('iExtra', 'vec2') as unknown as Node<'vec2'>;
    const vertexColor = attribute('color', 'vec3') as V3;
    const team = attribute('_team', 'float') as unknown as F;
    const minor = extra.x.lessThan(0);
    // Rounded: an interpolated whole number can land a hair under and pick the wrong actor texel.
    const owner = select(minor, extra.x.negate().sub(1), extra.x).round();

    const light = dot(vertexColor, vec3(...LUMA)).clamp(0, 1);
    const wall = screen(WALL_STONE).mul(mix(float(WALL_SHADE[0]), float(WALL_SHADE[1]), light));
    const roof = max(team, smoothstep(ROOF_UP[0], ROOF_UP[1], normalWorld.y));
    m.colorNode = mix(wall, this.deps.data.actorColor(owner, 0).mul(TEAM_GAIN), roof);

    const flat = select(minor, smoothstep(DECAL_FROM[0], DECAL_FROM[1], this.deps.camHeight), float(0));
    m.positionNode = positionLocal.mul(vec3(1, mix(float(1), float(DECAL_HEIGHT), flat), 1));
  }

  /** The draped layers changed: give the peaks a fresh material (their old one is disposed). */
  private refreshPeaks(): void {
    this.peaksStale = false;
    this.peakLayers = drapedLayers(this.deps.surface);
    const old = this.materials.get('peak');
    this.materials.delete('peak');
    const next = this.material('peak');
    for (const s of this.slots) {
      if (s?.def.kind === 'peak') {
        s.mesh.material = next;
      }
    }
    old?.dispose();
  }

  private material(kind: SlotDef['kind']): PaintedMaterial {
    const hit = this.materials.get(kind);
    if (hit) {
      return hit;
    }
    const m = new PaintedMaterial(this.style, { roughness: ROUGHNESS, metalness: 0 });
    const extra = attribute('iExtra', 'vec2') as unknown as Node<'vec2'>;
    const vertexColor = attribute('color', 'vec3') as V3;
    if (kind === 'peak') {
      this.paintPeak(m);
    } else if (kind === 'building') {
      this.paintBuilding(m);
    } else {
      const tint = extra.y.sub(0.5).mul(kind === 'tree' ? TREE_TINT_JITTER : TREE_TINT_JITTER * 0.5).add(1);
      const grade = kind === 'tree' ? vec3(...FOLIAGE_GRADE) : vec3(1);
      m.colorNode = screenNode(vertexColor).mul(grade).mul(tint);
    }
    if (kind === 'tree' || kind === 'plant') {
      const t = this.deps.time;
      const phase = extra.y.mul(Math.PI * 2);
      const bend = positionGeometry.y.mul(positionGeometry.y).mul(SWAY);
      const wave = vec3(sin(t.mul(Math.PI * 2 * SWAY_HZ[0]).add(phase)), 0, cos(t.mul(Math.PI * 2 * SWAY_HZ[1]).add(phase.mul(1.7))));
      m.positionNode = positionLocal.add(wave.mul(bend));
    }
    this.materials.set(kind, m);
    return m;
  }
}

/** Peak albedo: rock cooled with height on alpine models, then a glacier cap above the snow line. */
function peakColor(vertexColor: V3, alpine: F, snowLine: F): V3 {
  const high = smoothstep(COOL_FROM[0], COOL_FROM[1], positionGeometry.y).mul(alpine);
  const rock = screenNode(vertexColor).mul(mix(vec3(1), vec3(...ALPINE_COOL), high));

  const xz = vec2(wrapXNode(positionWorld.x), positionWorld.z);
  const ragged = worldFbm(xz, SNOW_NOISE_FREQ).sub(0.5).mul(SNOW_NOISE);
  const up = smoothstep(0.2, 0.75, normalWorld.y);
  const level = positionWorld.y.sub(snowLine).add(ragged).add(up.sub(0.5).mul(0.06));
  const snow = smoothstep(-SNOW_EDGE, SNOW_EDGE, level).mul(max(up, float(SNOW_STEEP)));
  const snowTone = mix(rgb(SNOW_SHADE), rgb(SNOW_LIT), up);
  return mix(rock, snowTone, snow);
}

/** The faceted mesh of a procedural peak slot (peakGeo.ts), flat-shaded. */
function peakGeometry(file: string): THREE.BufferGeometry {
  const spec = peakSpec(file);
  if (!spec) {
    throw new Error(`no peak model ${file}`);
  }
  const model = peakMesh(spec);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(model.position, 3));
  g.setAttribute('color', new THREE.BufferAttribute(model.color, 3));
  g.setAttribute('alpine', new THREE.BufferAttribute(model.alpine, 1));
  g.computeVertexNormals();
  return g;
}

/** Surface layers that also paint the peaks. */
function drapedLayers(surface: SurfaceChain): SurfaceLayer[] {
  return surface.list().filter(drapesOnProps);
}

function sameLayers(a: readonly SurfaceLayer[], b: readonly SurfaceLayer[]): boolean {
  return a.length === b.length && a.every((l, i) => l === b[i]);
}

/** The first mesh geometry in a loaded glTF scene, reduced to position, normal and colour. */
function firstGeometry(root: THREE.Object3D): THREE.BufferGeometry | null {
  let found: THREE.BufferGeometry | null = null;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (found || !mesh.isMesh) {
      return;
    }
    found = mesh.geometry;
  });
  const g = found as THREE.BufferGeometry | null;
  if (!g) {
    return null;
  }
  for (const name of Object.keys(g.attributes)) {
    if (name !== 'position' && name !== 'normal' && name !== 'color' && name !== '_team') {
      g.deleteAttribute(name);
    }
  }
  if (!g.getAttribute('_team')) {
    g.setAttribute('_team', new THREE.Float32BufferAttribute(new Float32Array(g.getAttribute('position').count), 1));
  }
  if (!g.getAttribute('color')) {
    const n = g.getAttribute('position').count;
    g.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(n * 3).fill(1), 3));
  }
  if (!g.getAttribute('normal')) {
    g.computeVertexNormals();
  }
  sanitizeNormals(g.getAttribute('normal') as THREE.BufferAttribute);
  return g;
}

/** Degenerate (zero-length) normals light as NaN, which the blur passes spread into white dots. */
function sanitizeNormals(n: THREE.BufferAttribute): void {
  for (let i = 0; i < n.count; i++) {
    const x = n.getX(i);
    const y = n.getY(i);
    const z = n.getZ(i);
    const len = Math.hypot(x, y, z);
    if (!(len > 1e-6)) {
      n.setXYZ(i, 0, 1, 0);
      continue;
    }
    n.setXYZ(i, x / len, y / len, z / len);
  }
  n.needsUpdate = true;
}

/** Column-major T · R_y(yaw) · S(sxz, sy, sxz) into `out` at `at`. */
function writeMatrix(out: Float32Array, at: number, x: number, y: number, z: number, yaw: number, sxz: number, sy: number): void {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  out[at] = c * sxz;
  out[at + 1] = 0;
  out[at + 2] = -s * sxz;
  out[at + 3] = 0;
  out[at + 4] = 0;
  out[at + 5] = sy;
  out[at + 6] = 0;
  out[at + 7] = 0;
  out[at + 8] = s * sxz;
  out[at + 9] = 0;
  out[at + 10] = c * sxz;
  out[at + 11] = 0;
  out[at + 12] = x;
  out[at + 13] = y;
  out[at + 14] = z;
  out[at + 15] = 1;
}

/** Low plants cast no shadow worth a second draw of their triangles. */
function castsShadow(def: SlotDef, shadows: 'on' | 'off'): boolean {
  return shadows === 'on' && def.kind !== 'plant';
}

/** Stable per-prop value in [0, 1) from its unshifted position. */
function seedOf(x: number, z: number): number {
  const v = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
